import React from "react";
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import {
  AuditStatusChip,
  HeaderTip,
  ManualProgress,
  PersonCell,
  ReadinessBar,
} from "./AuditCells";
import { AH_BORDER, AH_MUTED, AH_TEXT, AH_WHITE } from "../theme";
import { daysRemainingTone, formatDaysRemaining, formatShortDate } from "../utils";

const headSx = {
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.04em",
  color: AH_MUTED,
  backgroundColor: AH_WHITE,
  borderBottom: `1px solid ${AH_BORDER}`,
  whiteSpace: "normal",
  lineHeight: 1.25,
  py: 1.25,
  px: 0.75,
  textAlign: "center",
  overflow: "hidden",
};

/** Same body type as CSPM / Configuration tables. */
const cellSx = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  lineHeight: 1.4,
  color: "#334155",
  borderBottom: `1px solid ${AH_BORDER}`,
  py: 1.5,
  verticalAlign: "middle",
  textAlign: "center",
  overflow: "hidden",
};

const bodyTextSx = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  lineHeight: 1.4,
  color: "#334155",
  textAlign: "center",
};

const EmptyRows = ({ message, colSpan = 10 }) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ py: 6, borderBottom: 0 }}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", color: AH_MUTED }}>
        <SearchOffIcon sx={{ fontSize: 40, mb: 1, color: "#cbd5e1" }} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: AH_TEXT }}>{message}</Typography>
      </Box>
    </TableCell>
  </TableRow>
);

export const ActiveAuditsTable = ({ rows, onOpenAudit, onDownload }) => (
  <Box sx={{ width: "100%", mb: 3, boxSizing: "border-box" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.75 }}>
      <Typography sx={{ fontWeight: 700, color: AH_TEXT, fontSize: "1rem" }}>
        Active Audits
      </Typography>
      <Chip
        size="small"
        label={`${rows.length} audit${rows.length === 1 ? "" : "s"}`}
        sx={{ height: 22, fontSize: 11, fontWeight: 600, backgroundColor: "#fff", border: `1px solid ${AH_BORDER}` }}
      />
    </Box>
    <Box sx={{ border: `1px solid ${AH_BORDER}`, borderRadius: "12px", backgroundColor: AH_WHITE, overflow: "hidden" }}>
      <TableContainer sx={{ overflowX: "hidden" }}>
        <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>
                <HeaderTip label="AUDIT ID" title="Unique across organizations." />
              </TableCell>
              <TableCell sx={headSx}>
                <HeaderTip label="AUDIT NAME" title="Display name given at create time." />
              </TableCell>
              <TableCell sx={headSx}>
                <HeaderTip label="ACCOUNTS" title="accountCount on the engagement." />
              </TableCell>
              <TableCell sx={headSx}>
                <HeaderTip label="SCOPES" title="scopeCount. Each scope is one account and one framework." />
              </TableCell>
              <TableCell sx={{ ...headSx, width: "12%" }}>
                <HeaderTip label="AUDITOR" title="Audit owner. Owners and auditors are the two roles in CloudCatcher." />
              </TableCell>
              <TableCell sx={headSx}>
                <HeaderTip label="READINESS" title="auditScore from the API. Null means not yet scored — not 0%." />
              </TableCell>
              <TableCell sx={headSx}>
                <HeaderTip label="MANUAL CONTROLS" title="progress.decided / progress.total for manual controls only." />
              </TableCell>
              <TableCell sx={headSx}>
                <HeaderTip label="DAYS REMAINING" title="Days until the target completion date." />
              </TableCell>
              <TableCell sx={headSx}>STATUS</TableCell>
              <TableCell sx={headSx}>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <EmptyRows message="No active audits match the current filters." />
            ) : (
              rows.map((audit) => {
                const tone = daysRemainingTone(audit);
                return (
                  <TableRow key={audit.auditId} hover>
                    <TableCell sx={cellSx}>
                      <Typography sx={bodyTextSx}>{audit.auditCode}</Typography>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, maxWidth: 180 }}>
                      <Tooltip title={audit.name || ""}>
                        <Typography noWrap sx={bodyTextSx}>
                          {audit.name}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={cellSx}>{audit.accountCount ?? "—"}</TableCell>
                    <TableCell sx={cellSx}>{audit.scopeCount ?? "—"}</TableCell>
                    <TableCell sx={cellSx}>
                      <PersonCell name={audit.ownerEmail} />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <ReadinessBar value={audit.auditScore} />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <ManualProgress
                        decided={audit.progress?.decided}
                        total={audit.progress?.total}
                      />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Typography
                        sx={{
                          ...bodyTextSx,
                          color: tone === "overdue" ? "#dc2626" : tone === "warn" ? "#d97706" : "#334155",
                        }}
                      >
                        {formatDaysRemaining(audit)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <AuditStatusChip status={audit.status} />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                      <Tooltip title="Download report">
                        <IconButton size="small" onClick={() => onDownload(audit)} aria-label={`Download ${audit.auditCode}`}>
                          <DownloadOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Audit summary">
                        <IconButton size="small" onClick={() => onDownload(audit)} aria-label={`Summary ${audit.auditCode}`}>
                          <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Open in Compliance view">
                        <IconButton size="small" onClick={() => onOpenAudit(audit)} aria-label={`Open ${audit.auditCode}`}>
                          <OpenInNewOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  </Box>
);

export const CompletedAuditsTable = ({ rows, onDownload }) => (
  <Box sx={{ width: "100%", boxSizing: "border-box" }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.75, flexWrap: "wrap" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontWeight: 700, color: AH_TEXT, fontSize: "1rem" }}>
          Completed & Archived Audits
        </Typography>
        <Chip
          size="small"
          label={`${rows.length} report${rows.length === 1 ? "" : "s"}`}
          sx={{ height: 22, fontSize: 11, fontWeight: 600, backgroundColor: "#fff", border: `1px solid ${AH_BORDER}` }}
        />
      </Box>
      <Typography sx={{ fontSize: 12, color: AH_MUTED }}>
        Read-only final reports. Completed audits cannot be reopened.
      </Typography>
    </Box>
    <Box sx={{ border: `1px solid ${AH_BORDER}`, borderRadius: "12px", backgroundColor: AH_WHITE, overflow: "hidden" }}>
      <TableContainer sx={{ overflowX: "hidden" }}>
        <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>AUDIT ID</TableCell>
              <TableCell sx={headSx}>AUDIT NAME</TableCell>
              <TableCell sx={headSx}>ACCOUNTS</TableCell>
              <TableCell sx={headSx}>SCOPES</TableCell>
              <TableCell sx={{ ...headSx, width: "12%" }}>AUDITOR</TableCell>
              <TableCell sx={headSx}>FINAL SCORE</TableCell>
              <TableCell sx={headSx}>LAST ACTIVITY</TableCell>
              <TableCell sx={headSx}>STATUS</TableCell>
              <TableCell sx={headSx}>REPORT</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <EmptyRows message="No completed audits match the current filters." colSpan={9} />
            ) : (
              rows.map((audit) => (
                <TableRow key={audit.auditId} hover>
                  <TableCell sx={cellSx}>
                    <Typography sx={bodyTextSx}>{audit.auditCode}</Typography>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, maxWidth: 200 }}>
                    <Tooltip title={audit.name || ""}>
                      <Typography noWrap sx={bodyTextSx}>
                        {audit.name}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={cellSx}>{audit.accountCount ?? "—"}</TableCell>
                  <TableCell sx={cellSx}>{audit.scopeCount ?? "—"}</TableCell>
                  <TableCell sx={cellSx}>
                    <PersonCell name={audit.ownerEmail} />
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <ReadinessBar value={audit.auditScore} />
                  </TableCell>
                  <TableCell sx={cellSx}>{formatShortDate(audit.lastActivityAt)}</TableCell>
                  <TableCell sx={cellSx}>
                    <AuditStatusChip status={audit.status} />
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Tooltip title="Download report">
                      <IconButton size="small" onClick={() => onDownload(audit)} aria-label={`Download report ${audit.auditCode}`}>
                        <DownloadOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  </Box>
);

export const HubEmptyState = ({ onCreate }) => (
  <Box
    sx={{
      py: 8,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
    }}
  >
    <AssignmentOutlinedIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
    <Typography sx={{ fontSize: 16, fontWeight: 700, color: AH_TEXT, mb: 0.5 }}>
      No audits yet
    </Typography>
    <Typography sx={{ fontSize: 13, color: AH_MUTED, maxWidth: 420, mb: 2 }}>
      Create an audit for one account and one framework.
    </Typography>
    {onCreate}
  </Box>
);
